import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approveAllocation,
  cancelAllocation,
  getAllocationRejectionReasons,
  rejectAllocation,
} from '../api/allocationApi';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';
import { dashboardKeys } from '../../dashboard/hooks/dashboardKeys';
import { potKeys } from '../../finance/hooks/potKeys';
import { allocationDetailsKeys } from './useAllocationDetails';
import { allocationTimelineKeys } from './useAllocationEventsTimeline';
import { bookingDetailsKeys } from './useBookingDetails';

const rejectionReasonKeys = ['allocation-rejection-reasons'] as const;

export function useAllocationRejectionReasons(enabled: boolean) {
  return useQuery({
    queryKey: rejectionReasonKeys,
    queryFn: ({ signal }) => getAllocationRejectionReasons(signal),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllocationReview() {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['activity'] }),
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.unapprovedAllocations(),
      }),
      queryClient.invalidateQueries({ queryKey: allocationDetailsKeys.all }),
      queryClient.invalidateQueries({ queryKey: bookingDetailsKeys.all }),
      queryClient.invalidateQueries({ queryKey: allocationTimelineKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['allocations'] }),
      queryClient.invalidateQueries({ queryKey: potKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['sites'] }),
      queryClient.invalidateQueries({ queryKey: ['labourItems'] }),
    ]);

  const approve = useMutation({
    mutationFn: ({
      allocationId,
      notes,
    }: {
      allocationId: string;
      notes: string;
    }) => approveAllocation(allocationId, notes),
    onSuccess: async () => {
      await refresh();
      showSnackbar('Booking approved');
    },
  });

  const reject = useMutation({
    mutationFn: ({
      allocationId,
      reason,
      notes,
    }: {
      allocationId: string;
      reason: string;
      notes: string;
    }) => rejectAllocation(allocationId, reason, notes),
    onSuccess: async () => {
      await refresh();
      showSnackbar('Booking rejected');
    },
  });

  const cancel = useMutation({
    mutationFn: ({
      allocationId,
      notes,
      isVariation,
    }: {
      allocationId: string;
      notes: string;
      isVariation: boolean;
    }) => cancelAllocation(allocationId, notes, isVariation),
    onSuccess: async () => {
      await refresh();
      showSnackbar('Booking cancelled');
    },
  });

  return { approve, reject, cancel };
}
